import { buildChangeUsernameAction } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { EditUsernameScreenProps } from 'home/stack/types'
import { t } from 'app/i18l'
import React, { useCallback, useEffect, useState } from 'react'
import { errorAlert } from 'shared/alerts'
import { showHeaderButton } from 'shared/header/header_button'
import { useUserAccount } from 'users/queries/account'
import { usePerformUserProfileAction } from 'users/queries/perform_profile_action'
import { useRemoveUserPhoto } from 'users/queries/remove_photo'
import { useUpdateUserPhoto } from 'users/queries/update_photo'
import { EditUsernameView } from './view'

export function EditUsernameScreen({ navigation }: EditUsernameScreenProps) {
  const { theme } = useTheme()
  const { data: user } = useUserAccount()
  const uri = '/v2/users/me/'
  const updateAvatarMutation = useUpdateUserPhoto()
  const removeAvatarMutation = useRemoveUserPhoto()
  const [firstName, setFirstName] = useState(user?.firstName ?? '')
  const [lastName, setLastName] = useState(user?.lastName ?? '')
  const [isPhotoUploading, setIsPhotoUploading] = useState(false)

  const mutation = usePerformUserProfileAction(uri)

  const onAvatarChange = useCallback(
    async (avatarUrl: string, mimeType: string) => {
      setIsPhotoUploading(true)
      try {
        await updateAvatarMutation.mutateAsync({
          photoUrl: avatarUrl,
          mimeType,
        })
      } catch (err: any) {
        errorAlert(err, t('addMobileNumberErrorTitle'))
      }
      setIsPhotoUploading(false)
    },
    [updateAvatarMutation]
  )

  const onAvatarRemove = useCallback(async () => {
    await removeAvatarMutation.mutateAsync()
  }, [removeAvatarMutation])

  const onSubmit = useCallback(async () => {
    try {
      await mutation.mutateAsync(
        buildChangeUsernameAction(
          { method: 'update', uri },
          firstName,
          lastName
        )
      )
    } catch (err: any) {
      errorAlert(err, t('editUsernameErrorTitle'))
    }
    navigation.goBack()
  }, [firstName, lastName, navigation, mutation])

  useEffect(() => {
    showHeaderButton({
      navigation,
      title: t('done'),
      onPress: onSubmit,
      position: 'right',
      useIcon: false,
      theme: theme,
    })
  }, [navigation, onSubmit, theme])

  return (
    <EditUsernameView
      user={user}
      isPhotoUploading={isPhotoUploading}
      onAvatarChange={onAvatarChange}
      onAvatarRemove={onAvatarRemove}
      setFirstName={setFirstName}
      setLastName={setLastName}
    />
  )
}
